import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { IMG } from "./images";

const LanguageContext = createContext(null);

export const SERVICE_KEYS = ["gardiennage", "linge", "bateau", "courses"];
export const PROVIDER_KEYS = ["coach", "masseuse", "coiffeur", "chauffeur", "cuisinier", "babysitter"];
export const STATUS_KEYS = ["EN_ATTENTE", "VALIDEE", "REFUSEE", "EN_COURS", "TERMINEE"];

export const SECTORS = [
  "Cala Rossa", "Carbini", "Lecci", "Marina di Fiori", "Marina Rossa", "Palombaggia",
  "Pinarellu", "Porto-Vecchio", "Sainte-Lucie de Porto-Vecchio", "San Ciprianu",
  "Santa Giulia", "Trinité", "Autre",
];

const fr = {
  brand: "PRÉSENCE HOME SERVICES",
  tagline: "Se sentir en vacances comme chez soi, mais ailleurs.",
  hero_sub: "Conciergerie privée et locations en Corse-du-Sud.",
  nav: {
    concierge: "Conciergerie",
    services: "Services",
    locations: "Locations",
    providers: "Prestataires",
    contact: "Contact",
    reserve: "Réservez votre séjour",
    owner: "Espace propriétaire",
    admin: "Tableau de bord",
  },
  hero: {
    cta_tenants: "Locataires",
    cta_owners: "Propriétaires",
  },
  scenes: {
    s1: "Une présence discrète.",
    s2: "Un séjour pensé dans les moindres détails.",
    s3a: "Pour les propriétaires.",
    s3b: "Pour les voyageurs.",
  },
  about: {
    eyebrow: "La conciergerie",
    title: "Prendre soin des lieux, des séjours, et de tout ce qui les rend uniques.",
    p1: "Présence Home Services accompagne propriétaires et voyageurs en Corse-du-Sud, avec une même exigence : prendre soin des lieux, des séjours et de tout ce qui les rend uniques.",
    p2: "Pour les propriétaires, nous assurons la gestion et le suivi de leur bien tout au long de l'année. Attentifs à vos besoins, nous gérons votre maison par des passages réguliers et vous proposons un ensemble de prestations adaptées. Pour les voyageurs, nous organisons chaque détail du séjour afin qu'ils puissent simplement profiter.",
    p3: "Une présence discrète, attentive et locale, pour se sentir en vacances comme chez soi, mais ailleurs.",
  },
  owners: {
    eyebrow: "Propriétaires",
    title: "Votre bien, notre présence.",
    text: "Gestion de votre bien à l'année, suivi de la maison, préparation avant chaque arrivée, coordination des interventions, suivi des locations et relation avec les voyageurs : nous veillons sur votre propriété comme sur la nôtre, pour votre tranquillité.",
    cta: "Confier mon bien",
  },
  travelers: {
    eyebrow: "Voyageurs",
    title: "Votre séjour, tout simplement.",
    text: "Location de maisons et villas, préparation du séjour, services personnalisés, prestations à la demande et assistance pendant toute la durée de votre séjour : vous n'avez qu'une chose à faire, profiter.",
    cta: "On part ?",
  },
  search: {
    title: "Trouvez votre maison en Corse-du-Sud",
    type: "Type",
    all_types: "Tous les types",
    guests: "Personnes",
    pool: "Piscine",
    pool_yes: "Oui",
    pool_no: "Non",
    pool_any: "Peu importe",
    sector: "Localisation",
    sector_all: "Tous les secteurs",
    button: "Rechercher",
  },
  types: { villa: "Villa", maison: "Maison", appartement: "Appartement", studio: "Studio", autre: "Autre" },
  services: {
    eyebrow: "Nos services",
    title: "Des services pensés pour chaque séjour",
    sub: "Gardiennage, linge de maison, location de bateau, courses livrées à domicile : quatre services essentiels que nous assurons toute l'année, pour les propriétaires comme pour les voyageurs, avec la même exigence de qualité, de réactivité et de discrétion.",
    more: "Découvrir ce service",
    cta: "Demander ce service",
    avis: { title: "Ils sont désormais (plus) détendus", rating: "5,0", source: "Avis Google" },
    items: {
      gardiennage: {
        title: "Gardiennage",
        desc: "Une présence régulière dans votre maison, toute l'année. Ouvertures, vérifications, arrosage, petites interventions et rapports après chaque passage : votre bien reste vivant, même en votre absence.",
        info: "Passages planifiés ou à la demande · Rapport après chaque visite",
      },
      linge: {
        title: "Location de linge de maison",
        desc: "Draps, serviettes et linge de toilette de qualité hôtelière, préparés et disposés avant votre arrivée. Le linge est repris en fin de séjour, sans aucune contrainte pour vous.",
        info: "Qualité hôtelière · Installation et reprise incluses",
      },
      bateau: {
        title: "Location de bateau + services",
        desc: "Découvrez les criques et les plages sauvages du sud de la Corse par la mer. Location de bateau avec ou sans skipper, itinéraires conseillés et préparation complète de votre journée.",
        info: "Avec ou sans skipper · Itinéraires personnalisés",
      },
      courses: {
        title: "Courses & livraison à domicile",
        desc: "Vos courses sont faites et livrées avant votre arrivée ou pendant votre séjour : produits locaux, marché, cave, tout ce qu'il faut pour commencer vos vacances dès la première minute.",
        info: "Produits locaux privilégiés · Livraison à l'heure convenue",
      },
    },
  },
  providers: {
    eyebrow: "Nos prestataires",
    title: "Des prestataires de confiance, à la demande",
    sub: "Une sélection locale de professionnels que nous connaissons personnellement, disponibles pendant votre séjour.",
    more: "Découvrir ce prestataire",
    select_cta: "Ajouter à ma demande",
    cta_title: "Une question pour votre prochain séjour ?",
    cta_text: "Écrivez-nous : nous vous répondons rapidement et personnellement.",
    cta_button: "Contactez-nous",
    items: {
      coach: { title: "Coach sportif", desc: "Séances individuelles ou en petit groupe, à la maison, sur la plage ou en pleine nature. Renforcement, yoga, stretching : le coach s'adapte à votre rythme de vacances." },
      masseuse: { title: "Masseuse", desc: "Massages relaxants ou tonifiants directement à votre location. Une parenthèse de calme, face au maquis ou au son de la mer." },
      coiffeur: { title: "Coiffeur", desc: "Coiffure à domicile pour un soir, un événement ou simplement pour le plaisir. Coupe, brushing, coiffures d'occasion." },
      chauffeur: { title: "Chauffeur", desc: "Transferts aéroport, trajets vers les plages ou les villages, soirées sans contrainte : un chauffeur local discret et ponctuel." },
      cuisinier: { title: "Chef / Traiteur", desc: "Un chef à domicile ou un service traiteur pour vos repas et événements. Menus sur mesure, produits du terroir corse, buffets, apéritifs dînatoires ou grandes tablées, livrés ou servis directement à la villa." },
      babysitter: { title: "Babysitter", desc: "Garde d'enfants ponctuelle ou régulière par des professionnelles référencées, pour une soirée à deux ou une après-midi de liberté." },
    },
  },
  locations_page: {
    eyebrow: "Nos locations",
    title: "Découvrez tous nos biens",
    sub: "Chaque lieu est sélectionné, visité et préparé par nos soins, toute l'année. Villas avec piscine, maisons de charme ou appartements face à la mer : toutes nos annonces sont réunies ici — affinez simplement avec les filtres pour ne garder que celles qui vous ressemblent.",
    discover: "Découvrir",
    people: "personnes",
    pool: "Piscine",
    no_pool: "Sans piscine",
    no_results: "Aucun bien ne correspond à votre recherche pour le moment.",
  },
  property: {
    back: "Toutes les locations",
    guests: "Personnes",
    bedrooms: "Chambres",
    location: "Localisation",
    pool: "Piscine",
    pool_yes: "Oui",
    pool_no: "Non",
    description: "La maison",
    availability: "Disponibilités",
    available: "Disponible",
    unavailable: "Indisponible",
    reserve: "Réserver ce séjour",
    estimate_title: "Estimation",
    estimate_nights: "nuits",
    estimate_base: "Hébergement",
    estimate_extra: "Personnes supplémentaires",
    estimate_total: "Total estimé",
    estimate_note: "Estimation indicative, hors réductions éventuelles et frais de ménage.",
    share: "Partager l'annonce",
    copied: "Lien copié !",
    show_more: "Afficher la suite",
    show_less: "Réduire",
    watch_video: "Voir la vidéo",
    video: "La vidéo",
    airbnb: "Voir sur Airbnb",
    reviews: "Ils ont séjourné ici",
  },
  booking: {
    title: "Réservez votre séjour",
    sub: "Dites-nous tout, nous organisons le reste.",
    dates: "Dates",
    arrival: "Date d'arrivée",
    departure: "Date de départ",
    infos: "Informations personnelles",
    lastname: "Nom",
    firstname: "Prénom",
    phone: "Numéro de téléphone",
    email: "Adresse e-mail",
    stay: "Séjour",
    guests: "Nombre de personnes",
    property: "Location souhaitée",
    property_any: "Je ne sais pas encore",
    services: "Services souhaités",
    providers: "Prestataires souhaités",
    optional: "Optionnel",
    message_label: "Message (précisions, demandes particulières…)",
    submit: "Envoyer ma demande",
    sending: "Envoi en cours…",
    success: "Votre demande a bien été envoyée. Présence Home Services reviendra vers vous rapidement pour confirmer votre séjour et les prestations souhaitées.",
    error: "Une erreur est survenue. Merci de réessayer.",
  },
  contact: {
    eyebrow: "Contact",
    title: "Parlez nous de votre projet",
    sub: "Une question, un bien à confier, un séjour à organiser : écrivez-nous. Chaque demande est lue attentivement et nous vous répondons personnellement, rapidement, avec plaisir.",
    intro_owners: "Vous êtes propriétaire en Corse-du-Sud ? Confiez-nous votre bien en toute sérénité : shooting photo professionnel, rédaction et mise en ligne des annonces, gestion locative et administrative, gardiennage à l'année et coordination des prestataires. Vous gardez l'esprit libre, nous nous occupons de tout.",
    intro_travelers: "Vous préparez votre prochain séjour ? Décrivez-nous vos envies : nous sélectionnons la maison idéale et organisons chaque détail — arrivée, services, prestataires, imprévus — pour que vos vacances commencent dès le premier échange.",
    part1: "Vos informations",
    name: "Nom(s)",
    firstname: "Prénom(s)",
    email: "Mail",
    phone: "Téléphone",
    request_type: "Votre demande concerne",
    case_location: "Location",
    case_gestion: "Gestion",
    select_one: "Sélectionnez au moins une case : Location et/ou Gestion.",
    loc_section: "Votre séjour",
    loc_guests: "Nbr. de personnes",
    capacity_hint: "Capacité max : {n} personnes",
    guests_placeholder: "Nombre",
    loc_ref: "Ref. location",
    loc_ref_any: "Je ne sais pas encore",
    gest_section: "Votre bien",
    gest_type: "Type de bien",
    gest_sector: "Localisation",
    message: "Saisissez votre message (questions, demandes…)",
    booking_message: "Message (précisions, demandes particulières…)",
    send: "Envoyer ma demande",
    sending: "Envoi en cours…",
    success: "Votre demande a bien été envoyée. Présence Home Services reviendra vers vous rapidement.",
    error: "Une erreur est survenue. Merci de réessayer.",
    address: "Corse-du-Sud, France",
    phone_label: "Téléphone",
    email_label: "E-mail",
    feed_title: "LOCATION",
    feed_slogan: "Des annonces susceptibles de vous intéresser…",
  },
  footer: {
    navigation: "Navigation",
    reserve: "Réserver",
    legal: "Mentions légales",
    privacy: "Politique de confidentialité",
    created_by: "Créé par l'agence",
    site_vitrine: "Site vitrine",
    rights: "Tous droits réservés",
  },
  auth: {
    login_title: "Espace propriétaire",
    login_sub: "Connectez-vous pour consulter vos biens et vos réservations.",
    email: "Adresse e-mail",
    password: "Mot de passe",
    submit: "Se connecter",
    error: "Identifiants incorrects.",
    logout: "Se déconnecter",
    checking: "Chargement…",
  },
  owner: {
    title: "Espace propriétaire",
    welcome: "Bonjour",
    properties: "Mes biens",
    bookings: "Réservations",
    calendar: "Périodes indisponibles",
    no_properties: "Aucun bien n'est encore associé à votre compte.",
    no_bookings: "Aucune réservation pour le moment.",
    th_guest: "Voyageur",
    th_dates: "Dates",
    th_property: "Bien",
    th_guests: "Personnes",
    th_services: "Prestations",
    th_status: "Statut",
    tab_location: "Demandes de location",
    tab_gestion: "Demandes de gestion",
    no_location: "Aucune demande de location pour le moment.",
    no_gestion: "Aucune demande de gestion pour le moment.",
    from_contact: "Via formulaire de contact",
    welcome_photo: "Photo de bienvenue",
    accept: "Accepter",
    refuse: "Refuser",
    status_placeholder: "Traiter la demande",
    archives: "Demandes refusées (archives)",
    no_archives: "Aucune demande archivée.",
    stats: "Statistiques",
    kind: { location: "Location / séjour", gestion: "Gestion d'un bien", mixte: "Location + gestion", autre: "Autre demande" },
  },
  admin: {
    title: "Administration",
    group_admin: "Administration",
    group_site: "Site internet",
    site_hint: "Retrouvez ici tous les textes et images du site, organisés page par page. Cliquez sur le crayon pour modifier un texte ou remplacer une image.",
    site_texts: "Textes",
    site_images: "Images",
    site_edit: "Modifier",
    site_save: "Enregistrer",
    site_cancel: "Annuler",
    tab_bookings: "Demandes",
    tab_properties: "Biens",
    tab_owners: "Clients",
    clients_owners: "Propriétaires",
    clients_guests: "Locataires",
    no_guests: "Aucun locataire pour le moment.",
    filter_all: "Toutes",
    filter_pending: "En attente",
    filter_accepted: "Acceptées",
    filter_refused: "Refusées",
    filter_archived: "Archivées",
    stay_upcoming: "À venir",
    stay_ongoing: "En cours",
    stay_done: "Terminé",
    reply: "Répondre à la demande",
    mail_subject_location: "Présence Home Service - Demande de location",
    mail_subject_gestion: "Présence Home Service - Demande de gestion",
    back_owner_space: "Espace propriétaire",
    location_placeholder: "Choisissez un secteur",
    price_placeholder_day: "Prix par jour pour cette période (€)",
    booking_details: "Détail de la demande",
    assign_property: "Attribuer un bien",
    assign_property_placeholder: "Choisir un bien",
    assign_property_done: "Bien attribué",
    commission_title: "Commission",
    commission_pct_label: "Commission Carole (%)",
    commission_amount: "Commission calculée",
    stay_total: "Montant de la location",
    margot_pct_label: "Commission Margot (%)",
    margot_paid: "Payé",
    margot_base_note: "calculée sur la commission Carole",
    client_delete: "Supprimer",
    client_delete_confirm: "Supprimer ce client du répertoire ?",
    demande_delete_confirm: "Supprimer définitivement cette demande ? Cette action est irréversible.",
    message_delete_confirm: "Supprimer définitivement ce message ? Cette action est irréversible.",
    deleted: "Supprimé.",
    spec_guests_short: "PERS.",
    spec_bedrooms_short: "CHB.",
    spec_surface_short: "M²",
    selected_count: "sélectionnée(s)",
    delete_selected: "Supprimer la sélection",
    clear_selection: "Tout désélectionner",
    bulk_delete_confirm: "Supprimer définitivement les {n} éléments sélectionnés ? Cette action est irréversible.",
    client_edit: "Modifier",
    client_saved: "Coordonnées mises à jour.",
    stats_ca_owners: "CA locations (propriétaires)",
    stats_commission_caro: "Commission Carole",
    stats_commission_margot: "Commission Margot",
    margot_received: "Commission perçue",
    margot_due: "Commission à percevoir",
    tab_contacts: "Messages",
    new_property: "Ajouter un bien",
    edit: "Modifier",
    save: "Enregistrer",
    cancel: "Annuler",
    name: "Nom",
    location: "Localisation",
    type: "Type",
    guests: "Personnes",
    bedrooms: "Chambres",
    pool: "Piscine",
    surface: "Surface (m²)",
    images: "Photos",
    short_fr: "Titre (FR)",
    short_en: "Titre (EN)",
    desc_fr: "Description (FR)",
    desc_en: "Description (EN)",
    upload_images: "Ajouter des images",
    uploading: "Envoi…",
    drag_hint: "Glissez-déposez les photos pour choisir leur ordre.",
    ranges_title: "Calendrier — disponibilités & tarifs",
    range_hint: "Sélectionnez une période (ou un seul jour) dans le calendrier, puis bloquez-la comme indisponible ou appliquez-lui un tarif spécifique.",
    block_section: "Bloquer la période",
    price_section: "Tarif spécifique",
    add_range: "Ajouter la période",
    tenant_section: "Nouveau locataire",
    tenant_hint: "Sélectionnez les dates du séjour dans le calendrier, renseignez les informations du locataire, puis validez : le séjour est créé (compté dans les statistiques), la période est bloquée et le locataire rejoint le répertoire.",
    tenant_fn: "Prénom(s)",
    tenant_ln: "Nom(s)",
    tenant_email: "E-mail",
    tenant_phone: "Téléphone",
    tenant_amount: "Montant du séjour (€)",
    add_tenant: "Ajouter le locataire",
    tenant_added: "Locataire ajouté : séjour créé et période bloquée.",
    tab_seo: "Référencement SEO",
    seo_locked_hint: "Cette rubrique est protégée par un code d'accès.",
    seo_code_label: "Code d'accès",
    seo_unlock: "Déverrouiller",
    seo_wrong_code: "Code incorrect.",
    seo_pages_title: "Pages du site",
    seo_properties_title: "Annonces",
    seo_page_title: "Titre Google",
    seo_page_desc: "Méta-description",
    seo_page_slug: "Nom de l'URL",
    seo_slug_hint: "Laissez vide pour garder l'URL actuelle. L'ancienne URL continuera de fonctionner.",
    seo_save: "Enregistrer le référencement",
    seo_saved: "Référencement mis à jour.",
    seo_code_change: "Changer le code d'accès",
    seo_code_new: "Nouveau code",
    seo_code_changed: "Code d'accès mis à jour.",
    seo_slug_taken: "Cette URL est déjà utilisée.",
    no_ranges: "Aucune période indisponible pour le moment.",
    created: "Créé avec succès.",
    updated: "Mis à jour.",
    status_updated: "Statut mis à jour.",
    new_owner: "Créer un compte propriétaire",
    owner_name: "Nom",
    owner_email: "E-mail",
    owner_password: "Mot de passe",
    owner_properties: "Biens associés",
    owner_exists: "Un compte existe déjà avec cet e-mail.",
    no_bookings: "Aucune demande pour le moment.",
    no_contacts: "Aucun message pour le moment.",
    no_owners: "Aucun compte propriétaire.",
    video: "Vidéo",
    yes: "Oui",
    no: "Non",
    video_url: "URL de la vidéo",
    range_guest_fn: "Prénom du client (optionnel)",
    range_guest_ln: "Nom du client (optionnel)",
    ranges_summary: "Récapitulatif des périodes indisponibles",
    no_guest_name: "Nom du client non renseigné",
    guest_on: "Client",
    tab_reviews: "Avis",
    no_reviews: "Aucun avis pour le moment.",
    delete: "Supprimer",
    th_review: "Avis",
    send_review: "Demander un avis",
    review_sent_label: "Demande envoyée",
    review_request_sent: "Demande d'avis envoyée au client.",
    video_upload: "Importer une vidéo",
    video_uploading: "Envoi…",
    video_uploaded: "Vidéo importée",
    video_file: "Vidéo importée (depuis votre appareil)",
    airbnb: "Airbnb",
    airbnb_url: "Lien de l'annonce Airbnb",
    airbnb_ical_url: "Lien iCal du calendrier Airbnb",
    airbnb_ical_hint: "Sur Airbnb : Calendrier → Paramètres de disponibilité → Exporter le calendrier. Collez le lien .ics : les réservations Airbnb bloqueront automatiquement les dates sur le site (synchronisation toutes les 6 h et à chaque enregistrement).",
    external_sync_url: "Lien de synchronisation externe (autre site)",
    external_sync_hint: "Collez l'URL du calendrier d'un autre site (ex. URL DU PLANNING) : ses dates bloquées bloqueront automatiquement celles de cette annonce (synchronisation toutes les 6 h et à chaque enregistrement).",
    external_synced_count: "période(s) synchronisée(s) depuis le site externe",
    airbnb_sync_now: "Synchroniser maintenant",
    airbnb_synced_ok: "Calendrier Airbnb synchronisé.",
    airbnb_synced_count: "période(s) Airbnb synchronisée(s)",
    weekly_price: "Tarif semaine (€)",
    nightly_price: "Tarif nuit (€)",
    add_room: "Ajouter une chambre",
    tab_api: "API",
    api_email_title: "Email de demande d'avis",
    api_subject: "Objet du mail",
    api_heading: "Titre du mail",
    api_body: "Texte du mail",
    api_body_hint: "Utilisez {prenom} et {bien} : ils seront remplacés par le prénom du client et le nom de la villa louée.",
    api_cta_label: "Texte du bouton « avis »",
    api_cta_url: "Lien du bouton « avis »",
    api_ig_label: "Texte du bouton Instagram",
    api_ig_url: "Lien Instagram",
    api_photos: "Photos du mail (4 max)",
    api_photos_hint: "Cliquez sur des photos pour les ajouter au mail (elles renvoient vers la fiche du bien). Vide = photos automatiques de la villa louée.",
    api_save: "Enregistrer",
    api_saved: "Email mis à jour.",
    api_test: "Test mail",
    api_test_sent: "Email de test envoyé !",
    api_upload: "Importer une photo",
    delete_code_prompt: "Code de suppression :",
    delete_code_wrong: "Code incorrect.",
    tab_stats: "Statistiques",
    stats_booking_rate: "Taux de réservation",
    stats_sejours: "Séjours effectués",
    stats_ca_week: "CA semaine en cours",
    stats_ca_month: "CA mois en cours",
    stats_ca_year: "CA année en cours",
    stats_fill: "Taux de remplissage",
    stats_airbnb: "Clics Airbnb",
    stats_reviews: "Avis clients",
    stats_pending: "Demandes en attente",
    stats_per_property: "Rapport par bien",
    stats_stays_by_month: "Locations par mois",
    stats_no_stay: "Aucune location enregistrée pour ce bien.",
    stats_monthly: "Chiffre d'affaires par mois",
    stats_status_chart: "Répartition des demandes",
    stats_fill_chart: "Taux de remplissage par bien",
    stats_th_property: "Bien",
    stats_th_airbnb: "Clics Airbnb",
    stats_th_bookings: "Demandes",
    stats_th_sejours: "Séjours",
    stats_th_ca: "CA",
    stats_th_occ: "Jours réservés /365",
    stats_th_fill: "Remplissage",
    stats_ca_hint: "CA calculé à partir des tarifs semaine renseignés et des séjours validés (au prorata des nuits).",
    stats_no_price: "Le chiffre d'affaires apparaît dès qu'un séjour confirmé ou terminé est couvert par un tarif (semaine ou période).",
    dissociate_rooms: "Dissocier les pièces",
    set_cover: "Définir en couverture",
    cover_badge: "Couverture",
    room_sejour: "Séjour",
    room_salle_a_manger: "Salle à manger",
    room_cuisine: "Cuisine",
    room_chambre_1: "Chambre 1",
    room_chambre_2: "Chambre 2 (option)",
    room_chambre_3: "Chambre 3 (option)",
    room_chambre_4: "Chambre 4 (option)",
    room_chambre_5: "Chambre 5 (option)",
    room_salle_de_bain_1: "Salle de bain 1",
    room_salle_de_bain_2: "Salle de bain 2 (option)",
    room_salle_de_bain_3: "Salle de bain 3 (option)",
    room_salle_de_bain_4: "Salle de bain 4 (option)",
    room_salle_de_bain_5: "Salle de bain 5 (option)",
    room_exterieur: "Extérieur (option)",
    room_bureau: "Bureau (option)",
    pricing_title: "Tarifs & frais",
    fees_title: "Frais :",
    discounts_title: "Remise(s) :",
    fee_person: "Personne supplémentaire",
    fee_cleaning: "Ménage",
    disc_short: "Courts séjours",
    disc_long: "Longs séjours",
    nights_max: "≤ nuits",
    nights_min: "≥ nuits",
    price_periods: "Tarifs par période",
    price_period_hint: "Les tarifs par période se définissent depuis le calendrier ci-dessus ; ils sont récapitulés ici.",
    price_per_week: "Prix par semaine pour cette période (€)",
    price_per_night: "Prix pour cette nuit (€)",
    add_price: "Ajouter le tarif",
    no_prices: "Aucun tarif spécifique.",
    price_unit_week: "€/sem.",
    price_unit_night: "€/nuit",
    sim_title: "Simulateur de prix",
    sim_hint: "Estimez le tarif d'un séjour selon la configuration tarifaire ci-dessus (2 personnes incluses dans le tarif de base).",
    sim_arrival: "Arrivée",
    sim_departure: "Départ",
    sim_guests: "Personnes",
    sim_compute: "ESTIMATION (€)",
    sim_nights: "nuits",
    sim_base: "Hébergement",
    sim_discount: "Remise",
    sim_cleaning: "Ménage",
    sim_extra: "Personnes supplémentaires",
    sim_total: "Total estimé",
    sim_invalid: "Sélectionnez une arrivée et un départ valides.",
    sim_no_price: "Renseignez un tarif semaine ou des tarifs par période pour lancer une simulation.",
    extra_person_fee: "Frais par personne supplémentaire (€/nuit)",
    cleaning_fee: "Frais de ménage (€/séjour)",
    short_stay_discount: "Remise court séjour (%)",
    short_stay_max_nights: "Court séjour : nb max de nuits",
    long_stay_discount: "Remise long séjour (%)",
    long_stay_min_nights: "Long séjour : nb min de nuits",
    hide: "Masquer",
    show: "Afficher",
    hidden_badge: "Annonce masquée",
    delete_confirm: "Supprimer définitivement ce bien ? Cette action est irréversible.",
  },
  review: {
    title: "Votre avis compte",
    sub: "Merci pour votre séjour ! Partagez votre expérience en quelques mots.",
    stay: "Votre séjour",
    rating: "Votre note",
    message: "Votre avis (quelques mots sur votre séjour)",
    submit: "Publier mon avis",
    sending: "Envoi…",
    success_title: "Merci pour votre avis !",
    success_text: "Votre retour a bien été enregistré. Merci pour votre confiance.",
    already: "Vous avez déjà partagé votre avis pour ce séjour. Merci !",
    invalid: "Ce lien est invalide ou a expiré.",
    error: "Une erreur est survenue. Merci de réessayer.",
    home: "Retour à l'accueil",
  },
  status: { EN_ATTENTE: "En attente", VALIDEE: "Validée", REFUSEE: "Refusée", EN_COURS: "Séjour en cours", TERMINEE: "Terminé" },
  gallery: {
    discover_all: "Découvrir tous les biens",
    filter_all: "Toutes",
    place: "Lieu",
    surface: "Surface",
    people: "Nbr de personnes",
    rooms: "Chambre",
    price_from: "À partir de",
    per_week: "sem.",
    per_night: "nuit",
    ig_follow: "Suivez-nous sur Instagram",
    quote: "Le vrai luxe, c'est le temps que l'on ne compte plus.",
  },
};

const en = {
  brand: "PRÉSENCE HOME SERVICES",
  tagline: "Feel on holiday, at home — but elsewhere.",
  hero_sub: "Private concierge and seasonal rentals in South Corsica.",
  nav: {
    concierge: "Concierge",
    services: "Services",
    locations: "Rentals",
    providers: "Providers",
    contact: "Contact",
    reserve: "Book your stay",
    owner: "Owner area",
    admin: "Dashboard",
  },
  hero: {
    cta_tenants: "Guests",
    cta_owners: "Owners",
  },
  scenes: {
    s1: "A discreet presence.",
    s2: "A stay thought through to the smallest detail.",
    s3a: "For owners.",
    s3b: "For travellers.",
  },
  about: {
    eyebrow: "The concierge",
    title: "Caring for places, stays, and everything that makes them unique.",
    p1: "Présence Home Services supports owners and travellers in South Corsica with a single standard: caring for places, stays and everything that makes them unique.",
    p2: "For owners, we manage and look after their property all year round. Attentive to your needs, we care for your house with regular visits and offer a range of tailored services. For travellers, we organise every detail of the stay so they can simply enjoy.",
    p3: "A discreet, attentive and local presence, to feel on holiday, at home — but elsewhere.",
  },
  owners: {
    eyebrow: "Owners",
    title: "Your property, our presence.",
    text: "Year-round management of your property, house checks, preparation before every arrival, coordination of works, rental follow-up and guest relations: we look after your property as if it were our own, for your peace of mind.",
    cta: "Entrust my property",
  },
  travelers: {
    eyebrow: "Travellers",
    title: "Your stay, simply.",
    text: "House and villa rentals, stay preparation, personalised services, on-demand experiences and assistance throughout your stay: all you have to do is enjoy.",
    cta: "Shall we go?",
  },
  search: {
    title: "Find your house in South Corsica",
    type: "Type",
    all_types: "All types",
    guests: "Guests",
    pool: "Pool",
    pool_yes: "Yes",
    pool_no: "No",
    pool_any: "No preference",
    sector: "Location",
    sector_all: "All areas",
    button: "Search",
  },
  types: { villa: "Villa", maison: "House", appartement: "Apartment", studio: "Studio", autre: "Other" },
  services: {
    eyebrow: "Our services",
    title: "Services designed for every stay",
    sub: "Property watching, household linen, boat rental, grocery delivery: four essential services we provide all year round, for owners and travellers alike, with the same standards of quality, responsiveness and discretion.",
    more: "Discover this service",
    cta: "Request this service",
    avis: { title: "They are now (even more) relaxed", rating: "5.0", source: "Google reviews" },
    items: {
      gardiennage: {
        title: "Property watching",
        desc: "A regular presence in your home, all year round. Openings, checks, watering, small works and a report after each visit: your property stays alive, even when you are away.",
        info: "Scheduled or on-demand visits · Report after each visit",
      },
      linge: {
        title: "Household linen rental",
        desc: "Hotel-quality sheets, towels and bath linen, prepared and arranged before your arrival. Everything is collected at the end of the stay, with no effort on your side.",
        info: "Hotel quality · Delivery and collection included",
      },
      bateau: {
        title: "Boat rental + services",
        desc: "Discover the wild coves and beaches of South Corsica from the sea. Boat rental with or without skipper, suggested itineraries and full preparation of your day.",
        info: "With or without skipper · Tailored itineraries",
      },
      courses: {
        title: "Grocery shopping & home delivery",
        desc: "Your shopping is done and delivered before your arrival or during your stay: local products, market, cellar — everything you need to start your holiday from the very first minute.",
        info: "Local products first · Delivered at your chosen time",
      },
    },
  },
  providers: {
    eyebrow: "Our providers",
    title: "Trusted providers, on demand",
    sub: "A local selection of professionals we know personally, available throughout your stay.",
    more: "Discover this provider",
    select_cta: "Add to my request",
    cta_title: "A question about your next stay?",
    cta_text: "Write to us — we answer quickly and personally.",
    cta_button: "Contact us",
    items: {
      coach: { title: "Sports coach", desc: "Private or small-group sessions, at the house, on the beach or in the open air. Strength training, yoga, stretching: the coach adapts to your holiday rhythm." },
      masseuse: { title: "Masseuse", desc: "Relaxing or invigorating massages directly at your rental. A moment of calm, facing the maquis or to the sound of the sea." },
      coiffeur: { title: "Hairdresser", desc: "At-home hairdressing for an evening, an event or simply for pleasure. Cut, blow-dry, occasion styling." },
      chauffeur: { title: "Private driver", desc: "Airport transfers, rides to beaches or villages, carefree evenings: a discreet, punctual local driver." },
      cuisinier: { title: "Chef / Caterer", desc: "A private chef at your villa or a catering service for your meals and events. Tailor-made menus, Corsican terroir products, buffets, cocktail dinners or large family tables, delivered or served directly at the villa." },
      babysitter: { title: "Babysitter", desc: "Occasional or regular childcare by vetted professionals, for an evening for two or an afternoon of freedom." },
    },
  },
  locations_page: {
    eyebrow: "Our rentals",
    title: "Discover all our properties",
    sub: "Every place is selected, visited and prepared by our team, all year round. Villas with pools, charming houses or seafront apartments: all our listings are gathered here — simply use the filters to keep only the ones that suit you.",
    discover: "Discover",
    people: "guests",
    pool: "Pool",
    no_pool: "No pool",
    no_results: "No property matches your search at the moment.",
  },
  property: {
    back: "All rentals",
    guests: "Guests",
    bedrooms: "Bedrooms",
    location: "Location",
    pool: "Pool",
    pool_yes: "Yes",
    pool_no: "No",
    description: "The house",
    availability: "Availability",
    available: "Available",
    unavailable: "Unavailable",
    reserve: "Book this stay",
    estimate_title: "Estimate",
    estimate_nights: "nights",
    estimate_base: "Accommodation",
    estimate_extra: "Additional guests",
    estimate_total: "Estimated total",
    estimate_note: "Indicative estimate, excluding possible discounts and cleaning fees.",
    share: "Share this listing",
    copied: "Link copied!",
    show_more: "Read more",
    show_less: "Show less",
    watch_video: "Watch the video",
    video: "The video",
    airbnb: "View on Airbnb",
    reviews: "They stayed here",
  },
  booking: {
    title: "Book your stay",
    sub: "Tell us everything, we organise the rest.",
    dates: "Dates",
    arrival: "Arrival date",
    departure: "Departure date",
    infos: "Personal information",
    lastname: "Last name",
    firstname: "First name",
    phone: "Phone number",
    email: "Email address",
    stay: "Stay",
    guests: "Number of guests",
    property: "Preferred rental",
    property_any: "Not sure yet",
    services: "Requested services",
    providers: "Requested providers",
    optional: "Optional",
    message_label: "Message (details, special requests…)",
    submit: "Send my request",
    sending: "Sending…",
    success: "Your request has been sent. Présence Home Services will get back to you shortly to confirm your stay and the requested services.",
    error: "Something went wrong. Please try again.",
  },
  contact: {
    eyebrow: "Contact",
    title: "Tell us about your project",
    sub: "A question, a property to entrust, a stay to organise: write to us. Every request is read carefully and we answer personally, quickly, with pleasure.",
    intro_owners: "Are you a property owner in South Corsica? Entrust us with your property with complete peace of mind: professional photo shoot, listing copywriting and publishing, rental and administrative management, year-round property watching and provider coordination. You keep a free mind, we take care of everything.",
    intro_travelers: "Planning your next stay? Tell us what you dream of: we select the ideal house and organise every detail — arrival, services, providers, the unexpected — so your holiday starts from the very first exchange.",
    part1: "Your information",
    name: "Last name(s)",
    firstname: "First name(s)",
    email: "Email",
    phone: "Phone",
    request_type: "Your request concerns",
    case_location: "Rental",
    case_gestion: "Management",
    select_one: "Please select at least one box: Rental and/or Management.",
    loc_section: "Your stay",
    loc_guests: "Number of guests",
    capacity_hint: "Max capacity: {n} guests",
    guests_placeholder: "Number",
    loc_ref: "Property ref.",
    loc_ref_any: "Not sure yet",
    gest_section: "Your property",
    gest_type: "Property type",
    gest_sector: "Location",
    message: "Write your message (questions, requests…)",
    booking_message: "Message (details, special requests…)",
    send: "Send my request",
    sending: "Sending…",
    success: "Your request has been sent. Présence Home Services will get back to you shortly.",
    error: "Something went wrong. Please try again.",
    address: "South Corsica, France",
    phone_label: "Phone",
    email_label: "Email",
    feed_title: "RENTALS",
    feed_slogan: "Listings you may be interested in…",
  },
  footer: {
    navigation: "Navigation",
    reserve: "Book",
    legal: "Legal notice",
    privacy: "Privacy policy",
    created_by: "Created by the agency",
    site_vitrine: "Showcase website",
    rights: "All rights reserved",
  },
  auth: {
    login_title: "Owner area",
    login_sub: "Sign in to view your properties and reservations.",
    email: "Email address",
    password: "Password",
    submit: "Sign in",
    error: "Incorrect credentials.",
    logout: "Sign out",
    checking: "Loading…",
  },
  owner: {
    title: "Owner area",
    welcome: "Hello",
    properties: "My properties",
    bookings: "Reservations",
    calendar: "Unavailable periods",
    no_properties: "No property is linked to your account yet.",
    no_bookings: "No reservation yet.",
    th_guest: "Guest",
    th_dates: "Dates",
    th_property: "Property",
    th_guests: "Guests",
    th_services: "Services",
    th_status: "Status",
    tab_location: "Rental requests",
    tab_gestion: "Management requests",
    no_location: "No rental request yet.",
    no_gestion: "No management request yet.",
    from_contact: "Via contact form",
    welcome_photo: "Welcome photo",
    accept: "Accept",
    refuse: "Decline",
    status_placeholder: "Handle the request",
    archives: "Declined requests (archive)",
    no_archives: "No archived request.",
    stats: "Statistics",
    kind: { location: "Rental / stay", gestion: "Property management", mixte: "Rental + management", autre: "Other request" },
  },
  admin: {
    title: "Administration",
    group_admin: "Administration",
    group_site: "Website",
    site_hint: "Find all the site's texts and images here, organised page by page. Click the pen to edit a text or replace an image.",
    site_texts: "Texts",
    site_images: "Images",
    site_edit: "Edit",
    site_save: "Save",
    site_cancel: "Cancel",
    tab_bookings: "Requests",
    tab_properties: "Properties",
    tab_owners: "Clients",
    clients_owners: "Owners",
    clients_guests: "Tenants",
    no_guests: "No tenants yet.",
    filter_all: "All",
    filter_pending: "Pending",
    filter_accepted: "Accepted",
    filter_refused: "Declined",
    filter_archived: "Archived",
    stay_upcoming: "Upcoming",
    stay_ongoing: "Ongoing",
    stay_done: "Completed",
    reply: "Reply to the request",
    mail_subject_location: "Présence Home Service - Demande de location",
    mail_subject_gestion: "Présence Home Service - Demande de gestion",
    back_owner_space: "Owner space",
    location_placeholder: "Choose an area",
    price_placeholder_day: "Price per day for this period (€)",
    booking_details: "Request details",
    assign_property: "Assign a property",
    assign_property_placeholder: "Choose a property",
    assign_property_done: "Property assigned",
    commission_title: "Commission",
    commission_pct_label: "Carole's commission (%)",
    commission_amount: "Computed commission",
    stay_total: "Rental amount",
    margot_pct_label: "Margot's commission (%)",
    margot_paid: "Paid",
    margot_base_note: "calculated on Carole's commission",
    client_delete: "Delete",
    client_edit: "Edit",
    client_saved: "Details updated.",
    client_delete_confirm: "Remove this client from the directory?",
    demande_delete_confirm: "Permanently delete this request? This action cannot be undone.",
    message_delete_confirm: "Permanently delete this message? This action cannot be undone.",
    deleted: "Deleted.",
    spec_guests_short: "PAX",
    spec_bedrooms_short: "BDR",
    spec_surface_short: "M²",
    selected_count: "selected",
    delete_selected: "Delete selection",
    clear_selection: "Clear selection",
    bulk_delete_confirm: "Permanently delete the {n} selected items? This action cannot be undone.",
    stats_ca_owners: "Rental revenue (owners)",
    stats_commission_caro: "Carole's commission",
    stats_commission_margot: "Margot's commission",
    margot_received: "Commission received",
    margot_due: "Commission outstanding",
    tab_contacts: "Messages",
    new_property: "Add a property",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    name: "Name",
    location: "Location",
    type: "Type",
    guests: "Guests",
    bedrooms: "Bedrooms",
    pool: "Pool",
    surface: "Surface (sqm)",
    images: "Photos",
    short_fr: "Title (FR)",
    short_en: "Title (EN)",
    desc_fr: "Description (FR)",
    desc_en: "Description (EN)",
    upload_images: "Add images",
    uploading: "Uploading…",
    drag_hint: "Drag and drop photos to choose their order.",
    ranges_title: "Calendar — availability & pricing",
    range_hint: "Select a period (or a single day) in the calendar, then block it as unavailable or apply a specific price.",
    block_section: "Block this period",
    price_section: "Specific price",
    add_range: "Add the period",
    tenant_section: "New tenant",
    tenant_hint: "Select the stay dates in the calendar, fill in the tenant details, then confirm: the stay is created (counted in statistics), the period is blocked and the tenant joins the directory.",
    tenant_fn: "First name(s)",
    tenant_ln: "Last name(s)",
    tenant_email: "Email",
    tenant_phone: "Phone",
    tenant_amount: "Stay amount (€)",
    add_tenant: "Add tenant",
    tenant_added: "Tenant added: stay created and period blocked.",
    tab_seo: "SEO",
    seo_locked_hint: "This section is protected by an access code.",
    seo_code_label: "Access code",
    seo_unlock: "Unlock",
    seo_wrong_code: "Incorrect code.",
    seo_pages_title: "Site pages",
    seo_properties_title: "Listings",
    seo_page_title: "Google title",
    seo_page_desc: "Meta description",
    seo_page_slug: "URL name",
    seo_slug_hint: "Leave empty to keep the current URL. The old URL will keep working.",
    seo_save: "Save SEO",
    seo_saved: "SEO updated.",
    seo_code_change: "Change the access code",
    seo_code_new: "New code",
    seo_code_changed: "Access code updated.",
    seo_slug_taken: "This URL is already in use.",
    no_ranges: "No unavailable period for now.",
    created: "Created successfully.",
    updated: "Updated.",
    status_updated: "Status updated.",
    new_owner: "Create an owner account",
    owner_name: "Name",
    owner_email: "Email",
    owner_password: "Password",
    owner_properties: "Linked properties",
    owner_exists: "An account already exists with this email.",
    no_bookings: "No request yet.",
    no_contacts: "No message yet.",
    no_owners: "No owner account.",
    video: "Video",
    yes: "Yes",
    no: "No",
    video_url: "Video URL",
    range_guest_fn: "Guest first name (optional)",
    range_guest_ln: "Guest last name (optional)",
    ranges_summary: "Summary of unavailable periods",
    no_guest_name: "Guest name not provided",
    guest_on: "Guest",
    tab_reviews: "Reviews",
    no_reviews: "No review yet.",
    delete: "Delete",
    th_review: "Review",
    send_review: "Request a review",
    review_sent_label: "Request sent",
    review_request_sent: "Review request sent to the guest.",
    video_upload: "Upload a video",
    video_uploading: "Uploading…",
    video_uploaded: "Video uploaded",
    video_file: "Uploaded video (from your device)",
    airbnb: "Airbnb",
    airbnb_url: "Airbnb listing link",
    airbnb_ical_url: "Airbnb calendar iCal link",
    airbnb_ical_hint: "On Airbnb: Calendar → Availability settings → Export calendar. Paste the .ics link: Airbnb bookings will automatically block the dates on the site (synced every 6 hours and on each save).",
    external_sync_url: "External sync link (other website)",
    external_sync_hint: "Paste another website's calendar URL (e.g. PLANNING URL): its blocked dates will automatically block this listing's dates (synced every 6 hours and on each save).",
    external_synced_count: "period(s) synced from the external site",
    airbnb_sync_now: "Sync now",
    airbnb_synced_ok: "Airbnb calendar synced.",
    airbnb_synced_count: "synced Airbnb period(s)",
    weekly_price: "Weekly price (€)",
    nightly_price: "Nightly rate (€)",
    add_room: "Add a bedroom",
    tab_api: "API",
    api_email_title: "Review request email",
    api_subject: "Email subject",
    api_heading: "Email heading",
    api_body: "Email body",
    api_body_hint: "Use {prenom} and {bien}: they will be replaced by the guest's first name and the rented property name.",
    api_cta_label: "Review button label",
    api_cta_url: "Review button link",
    api_ig_label: "Instagram button label",
    api_ig_url: "Instagram link",
    api_photos: "Email photos (4 max)",
    api_photos_hint: "Click photos to add them to the email (they link to the property page). Empty = automatic photos of the rented property.",
    api_save: "Save",
    api_saved: "Email updated.",
    api_test: "Test email",
    api_test_sent: "Test email sent!",
    api_upload: "Upload a photo",
    delete_code_prompt: "Deletion code:",
    delete_code_wrong: "Incorrect code.",
    tab_stats: "Statistics",
    stats_booking_rate: "Booking rate",
    stats_sejours: "Completed stays",
    stats_ca_week: "Revenue this week",
    stats_ca_month: "Revenue this month",
    stats_ca_year: "Revenue this year",
    stats_fill: "Occupancy rate",
    stats_airbnb: "Airbnb clicks",
    stats_reviews: "Guest reviews",
    stats_pending: "Pending requests",
    stats_per_property: "Report per property",
    stats_stays_by_month: "Rentals by month",
    stats_no_stay: "No rentals recorded for this property.",
    stats_monthly: "Revenue per month",
    stats_status_chart: "Requests breakdown",
    stats_fill_chart: "Occupancy rate per property",
    stats_th_property: "Property",
    stats_th_airbnb: "Airbnb clicks",
    stats_th_bookings: "Requests",
    stats_th_sejours: "Stays",
    stats_th_ca: "Revenue",
    stats_th_occ: "Booked days /365",
    stats_th_fill: "Occupancy",
    stats_ca_hint: "Revenue computed from weekly prices and confirmed stays (prorated per night).",
    stats_no_price: "Revenue appears once a confirmed or completed stay is covered by a price (weekly or period).",
    dissociate_rooms: "Separate rooms",
    set_cover: "Set as cover",
    cover_badge: "Cover",
    room_sejour: "Living room",
    room_salle_a_manger: "Dining room",
    room_cuisine: "Kitchen",
    room_chambre_1: "Bedroom 1",
    room_chambre_2: "Bedroom 2 (optional)",
    room_chambre_3: "Bedroom 3 (optional)",
    room_chambre_4: "Bedroom 4 (optional)",
    room_chambre_5: "Bedroom 5 (optional)",
    room_salle_de_bain_1: "Bathroom 1",
    room_salle_de_bain_2: "Bathroom 2 (optional)",
    room_salle_de_bain_3: "Bathroom 3 (optional)",
    room_salle_de_bain_4: "Bathroom 4 (optional)",
    room_salle_de_bain_5: "Bathroom 5 (optional)",
    room_exterieur: "Outdoor (optional)",
    room_bureau: "Office (optional)",
    pricing_title: "Pricing & fees",
    fees_title: "Fees:",
    discounts_title: "Discount(s):",
    fee_person: "Additional guest",
    fee_cleaning: "Cleaning",
    disc_short: "Short stays",
    disc_long: "Long stays",
    nights_max: "≤ nights",
    nights_min: "≥ nights",
    price_periods: "Prices per period",
    price_period_hint: "Prices per period are set from the calendar above; they are summarised here.",
    price_per_week: "Price per week for this period (€)",
    price_per_night: "Price for this night (€)",
    add_price: "Add price",
    no_prices: "No specific price.",
    price_unit_week: "€/wk.",
    price_unit_night: "€/night",
    sim_title: "Price simulator",
    sim_hint: "Estimate the price of a stay based on the pricing configuration above (2 guests included in the base rate).",
    sim_arrival: "Arrival",
    sim_departure: "Departure",
    sim_guests: "Guests",
    sim_compute: "ESTIMATE (€)",
    sim_nights: "nights",
    sim_base: "Accommodation",
    sim_discount: "Discount",
    sim_cleaning: "Cleaning",
    sim_extra: "Additional guests",
    sim_total: "Estimated total",
    sim_invalid: "Select a valid arrival and departure.",
    sim_no_price: "Set a weekly price or period prices to run a simulation.",
    extra_person_fee: "Fee per additional guest (€/night)",
    cleaning_fee: "Cleaning fee (€/stay)",
    short_stay_discount: "Short stay discount (%)",
    short_stay_max_nights: "Short stay: max nights",
    long_stay_discount: "Long stay discount (%)",
    long_stay_min_nights: "Long stay: min nights",
    hide: "Hide",
    show: "Show",
    hidden_badge: "Listing hidden",
    delete_confirm: "Permanently delete this property? This action cannot be undone.",
  },
  review: {
    title: "Your review matters",
    sub: "Thank you for your stay! Share your experience in a few words.",
    stay: "Your stay",
    rating: "Your rating",
    message: "Your review (a few words about your stay)",
    submit: "Publish my review",
    sending: "Sending…",
    success_title: "Thank you for your review!",
    success_text: "Your feedback has been recorded. Thank you for your trust.",
    already: "You have already shared your review for this stay. Thank you!",
    invalid: "This link is invalid or has expired.",
    error: "Something went wrong. Please try again.",
    home: "Back to homepage",
  },
  status: { EN_ATTENTE: "Pending", VALIDEE: "Confirmed", REFUSEE: "Declined", EN_COURS: "Stay in progress", TERMINEE: "Completed" },
  gallery: {
    discover_all: "Discover all our properties",
    filter_all: "All",
    place: "Location",
    surface: "Surface",
    people: "Guests",
    rooms: "Bedroom",
    price_from: "From",
    per_week: "wk.",
    per_night: "night",
    ig_follow: "Follow us on Instagram",
    quote: "True luxury is time you no longer count.",
  },
};

export const dictionaries = { fr, en };

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState("fr");
  const [overrides, setOverrides] = useState({ texts: {}, images: {} });
  const toggle = useCallback(() => setLang((l) => (l === "fr" ? "en" : "fr")), []);

  const reloadContent = useCallback(async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content`);
      if (r.ok) setOverrides(await r.json());
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { reloadContent(); }, [reloadContent]);

  const t = useMemo(() => {
    const base = structuredClone(dictionaries[lang]);
    for (const [key, val] of Object.entries(overrides.texts || {})) {
      const text = val?.[lang];
      if (!text) continue;
      const parts = key.split(".");
      let node = base;
      let ok = true;
      for (let i = 0; i < parts.length - 1; i++) {
        if (node[parts[i]] == null || typeof node[parts[i]] !== "object") { ok = false; break; }
        node = node[parts[i]];
      }
      if (ok && typeof node[parts[parts.length - 1]] === "string") node[parts[parts.length - 1]] = text;
    }
    return base;
  }, [lang, overrides]);

  const simg = useCallback((key) => overrides.images?.[key] || IMG[key] || "", [overrides]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t, overrides, simg, reloadContent }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
